/**
 * API client tipizzato per il portale Tenacious C.
 *
 * Funzionalità:
 * - Inietta automaticamente l'header `Authorization: Bearer <token>` se presente
 * - Mappa le risposte non-2xx in ApiError con status e messaggio
 * - Base URL configurabile via variabili d'ambiente VITE_* (una per servizio)
 * - Funzione fetch iniettabile per facilitare il mocking nei test
 *
 * Variabili d'ambiente:
 *   VITE_PRODUCT_API       — base URL del product-service
 *   VITE_AGGREGATION_API   — base URL dell'aggregation-service
 *   VITE_SRP_API           — base URL dello srp-service
 *   VITE_NOTIFICATION_API  — base URL del notification-service
 *   VITE_MONITORING_API    — base URL del monitoring-service (E8, opzionale — usato
 *                            solo dal componente StatusBanner per verificare la freschezza
 *                            dei dati. Se non raggiungibile, il banner non si mostra.)
 *   VITE_DT_FRONTEND_URL   — URL base del frontend Dependency-Track (aperto in nuova tab).
 *                            Mostrato solo ai ruoli con capacita 'dt_link' abilitata.
 *                            Default dev: http://localhost:4221 (porta DEV_DT_FRONTEND_PORT).
 */

import { getToken, clearToken } from '../auth/tokenStore.js';
import { renewSession } from '../auth/session.js';

/** Errore API: include lo status HTTP e il messaggio estratto dalla risposta. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Base URL di ciascun servizio backend (configurable da env). */
export const SERVICE_URLS = {
  product: import.meta.env['VITE_PRODUCT_API'] ?? 'http://localhost:3001',
  aggregation: import.meta.env['VITE_AGGREGATION_API'] ?? 'http://localhost:3002',
  srp: import.meta.env['VITE_SRP_API'] ?? 'http://localhost:3003',
  notification: import.meta.env['VITE_NOTIFICATION_API'] ?? 'http://localhost:3004',
  /**
   * Monitoring-service (E8 — non ancora implementato).
   * Usato solo da StatusBanner/useDbFreshness per verificare la freschezza dei dati.
   * Se l'endpoint non è raggiungibile o risponde 404, il banner degrada silenziosamente.
   */
  monitoring: import.meta.env['VITE_MONITORING_API'] ?? 'http://localhost:3005',
} as const;

export type ServiceName = keyof typeof SERVICE_URLS;

/**
 * URL base del frontend Dependency-Track, aperto in nuova tab dal link nel nav.
 * Configurabile via VITE_DT_FRONTEND_URL; default dev = porta DEV_DT_FRONTEND_PORT (4221).
 */
export const DT_FRONTEND_URL: string =
  import.meta.env['VITE_DT_FRONTEND_URL'] ?? 'http://localhost:4221';

/** Opzioni per una singola richiesta API. */
export interface ApiRequestOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  /** Funzione fetch da usare (default: globalThis.fetch). Utile per il mocking nei test. */
  fetchFn?: typeof fetch;
  /** Segnale di annullamento (es. passato da TanStack Query per scartare richieste superate). */
  signal?: AbortSignal;
  /** `blob`: restituisce il body grezzo (download di file), senza deserializzare il JSON. */
  responseType?: 'json' | 'blob';
}

/**
 * Esegue una richiesta verso un servizio backend.
 *
 * @param service - Nome del servizio (product, aggregation, srp, notification)
 * @param path    - Path relativo (es. '/api/products')
 * @param options - Opzioni opzionali (method, body, headers, fetchFn custom)
 * @returns       - La risposta deserializzata come T
 * @throws ApiError se la risposta non è 2xx
 */
export async function apiRequest<T>(
  service: ServiceName,
  path: string,
  options: ApiRequestOptions = {},
): Promise<T> {
  const {
    method = 'GET',
    body,
    headers = {},
    fetchFn = globalThis.fetch,
    signal,
    responseType = 'json',
  } = options;

  const baseUrl = SERVICE_URLS[service];
  const url = `${baseUrl}${path}`;

  // Inietta il Bearer token se disponibile.
  // Content-Type solo quando c'è davvero un body: per richieste senza corpo (es. DELETE)
  // un Content-Type application/json con body vuoto fa rispondere 400 a Fastify
  // ("Body cannot be empty when content-type is set to 'application/json'").
  const send = (bearer: string | null) =>
    fetchFn(url, {
      method,
      headers: {
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
        ...headers,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      ...(signal ? { signal } : {}),
    });

  const token = getToken();
  let response = await send(token);

  // 401 con un token presente → token scaduto/invalido. Prima di arrendersi prova UNA volta
  // con un token fresco: quello già rinnovato nel frattempo (timer di AuthProvider, altro tab)
  // oppure uno nuovo dal refresh_token OIDC. Senza questo la sessione cade a ogni `exp`
  // dell'id_token, anche con l'utente attivo un attimo prima.
  // Salta se fetchFn è custom (contesto test: non c'è window.location da redirigere).
  if (response.status === 401 && token && fetchFn === globalThis.fetch) {
    const current = getToken();
    const fresh = current && current !== token ? current : await renewSession();
    if (fresh) response = await send(fresh);

    // Ancora 401 (o nessun rinnovo possibile): pulisci e reindirizza al login immediatamente,
    // senza attendere i retry di React Query (evita auth_failure a cascata).
    if (response.status === 401) {
      clearToken();
      window.location.href = '/login';
      // Lancia comunque l'errore: in-flight promises vengono rigettate, React Query si ferma.
      throw new ApiError(401, 'Sessione scaduta — effettua di nuovo il login.');
    }
  }

  if (!response.ok) {
    // Prova ad estrarre un messaggio di errore dal body JSON
    let message = `Errore HTTP ${response.status}`;
    try {
      const errBody = (await response.json()) as Record<string, unknown>;
      if (typeof errBody['message'] === 'string') {
        message = errBody['message'];
      } else if (typeof errBody['error'] === 'string') {
        message = errBody['error'];
      }
    } catch {
      // Il body non è JSON valido: usa il messaggio di default
    }
    throw new ApiError(response.status, message);
  }

  // Gestione 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  if (responseType === 'blob') return response.blob() as Promise<T>;
  return response.json() as Promise<T>;
}

/**
 * Factory: crea un client parzialmente applicato per un servizio specifico.
 * Utile per incapsulare il nome del servizio nelle singole feature API.
 *
 * @example
 *   const productApi = createServiceClient('product');
 *   const prodotti = await productApi<Prodotto[]>('/api/products');
 */
export function createServiceClient(service: ServiceName) {
  return function serviceRequest<T>(path: string, options?: ApiRequestOptions): Promise<T> {
    return apiRequest<T>(service, path, options);
  };
}
